import React from 'react'
import MuiTextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import BaseDisplayItem, {
    BaseDisplayItemProps,
    BaseDisplayItemState,
    DisplayItemBase,
    DisplayMode,
} from '@/feature/DisplayItems/BaseDisplayItem'
import {
    CurrencyConfig,
    formatWithCurrencyConfig,
    getCurrencyForLocale,
    isCurrencyConfig,
    normalizeLocaleCode,
} from '@/util/currencyFormat'
import { CurrencyOverrides as RuntimeCurrencyOverrides } from '@/util/environmentSettings'
import { truncateDecimalString } from '@/util/truncateDecimalString'

/* ---------- Types ---------- */

const DEFAULT_DISPLAY_DECIMAL_PLACES = 2
const DEFAULT_STORAGE_DECIMAL_PLACES = 4
type DecimalFormat =
    | ''
    | 'number'
    | 'currency'
    | 'percentage'
    | 'measurement'
    | 'rating'

interface ItemSettings {
    [key: string]: unknown
    decimals?: number // default 4
    allow_negative?: boolean // default true
    format?: DecimalFormat
    unit?: string
    unit_position?: 'before' | 'after'
    rating_max?: number
    // Legacy flat settings (still supported as fallback)
    currency?: string | CurrencyConfig // e.g. "EUR" or backend currency config
    locale?: string // e.g. "nl-NL"
    symbol?: string
    display_position?: 'before' | 'after'
    decimal_separator?: string
    thousands_separator?: string
    decimal_places?: number
    // rounding_mode?: 'half_up'
}

interface Item extends DisplayItemBase {
    settings?: ItemSettings
    mode?: DisplayMode
}

interface Props extends BaseDisplayItemProps<string, Item> {
    onKeyPress?: (
        itemOrId: Item | Item['id'],
        event: React.KeyboardEvent<HTMLElement>
    ) => void
}

interface State extends BaseDisplayItemState<string> {
    displayValue: string
}

/* ---------- Currency resolution ---------- */

/**
 * Reads the currency code plus flat currency_overrides setting from kedo env,
 * then maps them to the internal CurrencyConfig shape used by the formatter.
 */
function getEnvCurrencyConfig(props: Props): CurrencyConfig | null {
    try {
        const env = props.kedo?.env?.()
        const currency = env?.currency
        const runtimeOverrides = env?.currencyOverrides as
            | RuntimeCurrencyOverrides
            | undefined

        if (typeof currency !== 'string' || !currency) return null
        const locale =
            normalizeLocaleCode(
                env?.getSelectedLocale?.()?.code ?? props.kedo?.getLanguage?.()
            ) ?? 'nl-NL'

        return {
            code: currency,
            locale,
            display: runtimeOverrides?.display ?? 'symbol',
            overrides: {
                display_position: runtimeOverrides?.display_position,
                decimal_separator: runtimeOverrides?.decimal_separator,
                thousands_separator: runtimeOverrides?.thousands_separator,
                decimal_places: runtimeOverrides?.decimal_places,
            },
        }
    } catch {
        return null
    }
}

function getItemCurrencyConfig(
    itemSettings?: ItemSettings
): CurrencyConfig | null {
    const currency = itemSettings?.currency

    if (isCurrencyConfig(currency)) return currency

    if (typeof currency === 'string' && currency.trim() !== '') {
        const locale =
            typeof itemSettings?.locale === 'string'
                ? normalizeLocaleCode(itemSettings.locale) ?? 'nl-NL'
                : 'nl-NL'
        return {
            code: currency,
            locale,
            display: 'symbol',
            overrides: {
                display_position: itemSettings?.display_position,
                decimal_separator: itemSettings?.decimal_separator,
                thousands_separator: itemSettings?.thousands_separator,
                decimal_places: itemSettings?.decimal_places,
            },
        }
    }

    return null
}

function getCurrencyConfig(props: Props): CurrencyConfig | null {
    if (getDecimalFormat(props.item?.settings) !== 'currency') {
        return null
    }

    return (
        getItemCurrencyConfig(props.item?.settings) ??
        getEnvCurrencyConfig(props) ??
        getDefaultCurrencyConfig(props)
    )
}

function getDefaultCurrencyConfig(props: Props): CurrencyConfig | null {
    const env = props.kedo?.env?.()
    const userLocaleRaw =
        env?.getSelectedLocale?.()?.code ?? props.kedo?.getLanguage?.()
    const locale = normalizeLocaleCode(userLocaleRaw) ?? 'nl-NL'
    const code = getCurrencyForLocale(locale) ?? 'EUR'

    if (!code) return null

    return {
        code,
        locale,
        display: 'symbol',
        overrides: {
            display_position: 'before',
            decimal_separator: ',',
            thousands_separator: '.',
            decimal_places: DEFAULT_DISPLAY_DECIMAL_PLACES,
        },
    }
}

function getStorageDecimalPlaces(itemSettings?: ItemSettings): number {
    return typeof itemSettings?.decimals === 'number'
        ? itemSettings.decimals
        : DEFAULT_STORAGE_DECIMAL_PLACES
}

function getDisplayDecimalPlaces(
    itemSettings?: ItemSettings,
    currencyConfig?: CurrencyConfig | null
): number {
    if (typeof currencyConfig?.overrides?.decimal_places === 'number') {
        return currencyConfig.overrides.decimal_places
    }

    if (typeof itemSettings?.decimal_places === 'number') {
        return itemSettings.decimal_places
    }

    return DEFAULT_DISPLAY_DECIMAL_PLACES
}

function getDecimalSeparator(
    itemSettings?: ItemSettings,
    currencyConfig?: CurrencyConfig | null
): string {
    return (
        currencyConfig?.overrides?.decimal_separator ??
        itemSettings?.decimal_separator ??
        '.'
    )
}

function getDecimalFormat(itemSettings?: ItemSettings): DecimalFormat {
    const format = itemSettings?.format

    if (
        format === 'number' ||
        format === 'currency' ||
        format === 'percentage' ||
        format === 'measurement' ||
        format === 'rating'
    ) {
        return format
    }

    return ''
}

function getLocale(props: Props): string {
    const itemLocale =
        typeof props.item?.settings?.locale === 'string'
            ? props.item.settings.locale
            : undefined
    const userLocaleRaw =
        props.kedo?.env?.().getSelectedLocale?.()?.code ??
        props.kedo?.getLanguage?.()

    return normalizeLocaleCode(itemLocale ?? userLocaleRaw) ?? 'nl-NL'
}

/* ---------- Helpers ---------- */

function normalizeDecimalInput(
    raw: string,
    decimals: number,
    allowNegative: boolean
): string {
    if (raw == null) return ''

    // Trim + normalize comma to dot
    let v = raw.trim().replace(',', '.')

    // Remove everything except digits, dot, minus
    v = v.replace(/[^\d.-]/g, '')

    // Only allow a single leading "-"
    if (!allowNegative) {
        v = v.replace(/-/g, '')
    } else {
        v = v.replace(/(?!^)-/g, '') // remove any "-" not at start
    }

    // Only allow one dot
    const firstDot = v.indexOf('.')
    if (firstDot !== -1) {
        const before = v.slice(0, firstDot + 1)
        const after = v.slice(firstDot + 1).replace(/\./g, '') // remove extra dots
        v = before + after
    }

    // Enforce max decimals
    const dot = v.indexOf('.')
    if (dot !== -1 && decimals >= 0) {
        const intPart = v.slice(0, dot)
        const decPart = v.slice(dot + 1).slice(0, decimals)
        v = `${intPart}.${decPart}`
    }

    return v
}

function applySeparatorOverrides(
    value: string,
    locale: string,
    itemSettings?: ItemSettings
): string {
    if (
        !itemSettings?.decimal_separator &&
        !itemSettings?.thousands_separator
    ) {
        return value
    }

    const parts = new Intl.NumberFormat(locale).formatToParts(1234567.89)
    const intlDecimal = parts.find((p) => p.type === 'decimal')?.value ?? '.'
    const intlGroup = parts.find((p) => p.type === 'group')?.value ?? ','

    let result = value
    if (itemSettings.thousands_separator !== undefined) {
        result = result.split(intlGroup).join('\x00')
    }
    if (itemSettings.decimal_separator !== undefined) {
        result = result.split(intlDecimal).join(itemSettings.decimal_separator)
    }
    if (itemSettings.thousands_separator !== undefined) {
        result = result.split('\x00').join(itemSettings.thousands_separator)
    }

    return result
}

function formatNumber(
    value: string,
    decimalPlaces: number,
    locale: string,
    itemSettings?: ItemSettings
): string {
    const normalized = value.trim().replace(',', '.')
    const n = Number(normalized)
    if (!Number.isFinite(n)) return value

    const truncated = truncateDecimalString(normalized, decimalPlaces)
    const truncatedN = Number(truncated)

    const formatted = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimalPlaces,
        maximumFractionDigits: decimalPlaces,
    }).format(truncatedN)

    return applySeparatorOverrides(formatted, locale, itemSettings)
}

function getCurrencySymbol(locale: string, currency: string): string {
    return (
        new Intl.NumberFormat(locale, {
            style: 'currency',
            currency,
            currencyDisplay: 'narrowSymbol',
        })
            .formatToParts(0)
            .find((part) => part.type === 'currency')?.value ?? currency
    )
}

function formatForEdit(
    value: string | undefined,
    decimalPlaces: number,
    decimalSeparator = '.'
): string {
    if (!value) return ''

    const normalized = value.trim().replace(',', '.')
    const n = Number(normalized)
    if (!Number.isFinite(n)) return value

    return truncateDecimalString(normalized, decimalPlaces).replace(
        '.',
        decimalSeparator
    )
}

function isIncompleteDecimalInput(value: string): boolean {
    return (
        value === '' ||
        value === '-' ||
        value === '.' ||
        value === '-.' ||
        value.endsWith('.')
    )
}

function formatForStorage(value: string, decimals: number): string {
    if (isIncompleteDecimalInput(value)) return value

    const n = Number(value)
    if (!Number.isFinite(n)) return value

    return n.toFixed(decimals)
}

/* ---------- Component ---------- */

class DecimalField extends BaseDisplayItem<
    string,
    Item,
    HTMLInputElement,
    State
> {
    constructor(props: Props) {
        super(props)
        const currencyConfig = getCurrencyConfig(props)
        const decimalSeparator = getDecimalSeparator(
            props.item?.settings,
            currencyConfig
        )
        this.state = {
            ...this.state,
            displayValue: formatForEdit(
                props.value,
                getDisplayDecimalPlaces(props.item?.settings, currencyConfig),
                decimalSeparator
            ),
        }
        this.onChange = this.onChange.bind(this)
        this.onBlur = this.onBlur.bind(this)
    }

    private getDecimals(): number {
        return getStorageDecimalPlaces((this.props as Props).item?.settings)
    }

    private getDisplayDecimals(): number {
        const props = this.props as Props
        return getDisplayDecimalPlaces(
            props.item?.settings,
            this.getCurrencyConfig()
        )
    }

    private getDecimalSeparator(): string {
        const props = this.props as Props
        return getDecimalSeparator(
            props.item?.settings,
            this.getCurrencyConfig()
        )
    }

    private getAllowNegative(): boolean {
        return this.getSetting<boolean>('allow_negative', true) ?? true
    }

    private getFormat(): DecimalFormat {
        return getDecimalFormat((this.props as Props).item?.settings)
    }

    private getCurrencyConfig(): CurrencyConfig | null {
        return getCurrencyConfig(this.props as Props)
    }

    /* ---------- Change ---------- */

    onChange(event: React.ChangeEvent<HTMLInputElement>) {
        const raw = event.target.value
        const decimals = this.getDecimals()
        const displayDecimals = this.getDisplayDecimals()
        const allowNegative = this.getAllowNegative()
        const normalized = normalizeDecimalInput(raw, decimals, allowNegative)
        const displayValue = normalizeDecimalInput(
            raw,
            displayDecimals,
            allowNegative
        ).replace('.', this.getDecimalSeparator())
        const storageValue = formatForStorage(normalized, decimals)

        this.setState({
            value: storageValue,
            displayValue,
        })
        this.props.changeValue?.(this.props.item.id, storageValue)
    }

    onBlur() {
        const v = (this.state.value ?? '').trim()

        if (v === '' || v === '-' || v === '.' || v === '-.') {
            this.setState({
                value: '',
                displayValue: '',
            })
            this.props.changeValue?.(this.props.item.id, '')
            return
        }

        const n = Number(v)
        if (!Number.isFinite(n)) return
        const storageValue = n.toFixed(this.getDecimals())
        const displayValue = formatForEdit(
            storageValue,
            this.getDisplayDecimals(),
            this.getDecimalSeparator()
        )
        this.setState({
            value: storageValue,
            displayValue,
        })
        this.props.changeValue?.(this.props.item.id, storageValue)
    }

    componentDidUpdate(prevProps: Props) {
        if (prevProps.value !== this.props.value) {
            const nextValue = this.props.value ?? ''
            const currencyConfig = this.getCurrencyConfig()
            this.setState({
                value: nextValue,
                displayValue: formatForEdit(
                    nextValue,
                    getDisplayDecimalPlaces(
                        (this.props as Props).item?.settings,
                        currencyConfig
                    ),
                    getDecimalSeparator(
                        (this.props as Props).item?.settings,
                        currencyConfig
                    )
                ),
            })
        }
    }

    /* ---------- Render: SHOW ---------- */

    renderShow(): React.ReactNode {
        const props = this.props as Props
        if (
            props.value === null ||
            props.value === undefined ||
            props.value === ''
        ) {
            return null
        }

        const n = Number(props.value)
        if (!Number.isFinite(n)) return props.value

        const format = getDecimalFormat(props.item?.settings)
        const decimalPlaces = getDisplayDecimalPlaces(
            props.item?.settings,
            getCurrencyConfig(props)
        )
        const locale = getLocale(props)

        if (format === 'currency') {
            const currencyConfig = getCurrencyConfig(props)
            if (currencyConfig) {
                return formatWithCurrencyConfig(props.value, currencyConfig)
            }
        }

        const formattedNumber = formatNumber(
            props.value,
            decimalPlaces,
            locale,
            props.item?.settings
        )

        if (format === 'percentage') {
            return `${formattedNumber}%`
        }

        if (format === 'measurement' && props.item?.settings?.unit) {
            const unit = props.item.settings.unit
            return props.item.settings.unit_position === 'before'
                ? `${unit}\u00A0${formattedNumber}`
                : `${formattedNumber}\u00A0${unit}`
        }

        if (format === 'rating') {
            const max = props.item?.settings?.rating_max ?? 5
            return `${formattedNumber}/${max}`
        }

        return formattedNumber
    }

    /* ---------- Render: EDIT ---------- */

    renderEdit(): React.ReactNode {
        const props = this.props as Props
        const displayDecimals = this.getDisplayDecimals()
        const onKeyPress = props.onKeyPress
        const format = this.getFormat()
        const currencyConfig = this.getCurrencyConfig()
        const itemLocale =
            typeof props.item?.settings?.locale === 'string'
                ? props.item.settings.locale
                : undefined
        const itemCurrency =
            typeof props.item?.settings?.currency === 'string'
                ? props.item.settings.currency
                : undefined
        const itemSymbol =
            typeof props.item?.settings?.symbol === 'string'
                ? props.item.settings.symbol
                : undefined
        const userLocaleRaw =
            props.kedo?.env?.().getSelectedLocale?.()?.code ??
            props.kedo?.getLanguage?.()
        const locale =
            normalizeLocaleCode(
                currencyConfig?.locale ?? itemLocale ?? userLocaleRaw
            ) ?? 'nl-NL'
        const currency =
            currencyConfig?.code ?? itemCurrency ?? getCurrencyForLocale(locale)
        const symbol =
            itemSymbol ?? (currency ? getCurrencySymbol(locale, currency) : '')
        const symbolPosition =
            currencyConfig?.overrides?.display_position ??
            props.item?.settings?.display_position ??
            'before'
        const unit =
            typeof props.item?.settings?.unit === 'string'
                ? props.item.settings.unit
                : ''
        const unitPosition = props.item?.settings?.unit_position ?? 'after'
        const ratingMax = props.item?.settings?.rating_max ?? 5
        const startAdornment =
            format === 'currency' && symbol && symbolPosition === 'before'
                ? symbol
                : format === 'measurement' && unit && unitPosition === 'before'
                ? unit
                : undefined
        const endAdornment =
            format === 'currency' && symbol && symbolPosition === 'after'
                ? symbol
                : format === 'measurement' && unit && unitPosition === 'after'
                ? unit
                : format === 'percentage'
                ? '%'
                : format === 'rating'
                ? `/ ${ratingMax}`
                : undefined

        return (
            <MuiTextField
                size="small"
                fullWidth
                inputRef={this.innerRef}
                value={this.state.displayValue}
                error={this.isInvalid()}
                helperText={this.isInvalid() ? this.getErrorText() : ''}
                onChange={this.onChange}
                onBlur={this.onBlur}
                onKeyPress={
                    onKeyPress ? (e) => onKeyPress(props.item, e) : undefined
                }
                inputProps={{
                    inputMode: 'decimal',
                    pattern: `^-?\\d*[.,]?\\d{0,${displayDecimals}}$`,
                    maxLength: 1 + (12 - displayDecimals) + 1 + displayDecimals,
                }}
                InputProps={{
                    startAdornment: startAdornment ? (
                        <InputAdornment position="start">
                            {startAdornment}
                        </InputAdornment>
                    ) : undefined,
                    endAdornment: endAdornment ? (
                        <InputAdornment position="end">
                            {endAdornment}
                        </InputAdornment>
                    ) : undefined,
                }}
                placeholder={
                    displayDecimals > 0
                        ? `0${this.getDecimalSeparator()}${'0'.repeat(
                              displayDecimals
                          )}`
                        : '0'
                }
            />
        )
    }

    /* ---------- Render: SEARCH ---------- */

    renderSearch(): React.ReactNode {
        const props = this.props as Props
        const decimals = this.getDecimals()
        const allowNegative = this.getAllowNegative()
        const onKeyPress = props.onKeyPress

        return (
            <MuiTextField
                size="small"
                fullWidth
                type="text"
                value={props.value ?? ''}
                onChange={(e) => {
                    const normalized = normalizeDecimalInput(
                        e.target.value,
                        decimals,
                        allowNegative
                    )
                    props.changeValue?.(props.item.id, normalized)
                }}
                onKeyPress={
                    onKeyPress ? (e) => onKeyPress(props.item.id, e) : undefined
                }
                inputProps={{
                    inputMode: 'decimal',
                    pattern: '^-?\\d*[.,]?\\d*$',
                }}
            />
        )
    }
}

export default DecimalField
